import { useState } from 'react';
import { App, Dropdown, type MenuProps } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
  FileTextOutlined,
  FolderAddOutlined,
  FolderFilled,
  FolderOpenOutlined,
  MoreOutlined,
  RightOutlined,
} from '@ant-design/icons';
import type { Folder } from '@core/entities/Folder';
import { NamePromptModal } from '@presentation/components/workbench/NamePromptModal';
import { FolderDocsModal } from '@presentation/components/workbench/FolderDocsModal';
import { FolderTreeLevel } from '@presentation/components/workbench/FolderTreeLevel';
import { useCreateFolder, useDeleteFolder, useUpdateFolder } from '@presentation/hooks/useFolders';
import { useWorkbenchStore } from '@presentation/stores/workbenchStore';

interface FolderTreeNodeProps {
  workspaceId: string;
  folder: Folder;
  depth: number;
}

export const FolderTreeNode = ({ workspaceId, folder, depth }: FolderTreeNodeProps) => {
  const { message, modal } = App.useApp();
  const [expanded, setExpanded] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const openNewRequestTab = useWorkbenchStore((state) => state.openNewRequestTab);
  const updateFolder = useUpdateFolder(folder.id);
  const createFolder = useCreateFolder(folder.collectionId);
  const deleteFolder = useDeleteFolder(folder.collectionId, folder.parentId);

  const menuItems: MenuProps['items'] = [
    { key: 'new-request', icon: <FileAddOutlined />, label: 'New request' },
    { key: 'new-folder', icon: <FolderAddOutlined />, label: 'New folder' },
    { key: 'rename', icon: <EditOutlined />, label: 'Rename' },
    { key: 'docs', icon: <FileTextOutlined />, label: 'Documentation' },
    { type: 'divider' },
    { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'new-request') {
      openNewRequestTab(workspaceId, folder.collectionId, folder.id);
    } else if (key === 'new-folder') {
      setCreatingFolder(true);
    } else if (key === 'rename') {
      setRenaming(true);
    } else if (key === 'docs') {
      setDocsOpen(true);
    } else if (key === 'delete') {
      modal.confirm({
        title: `Delete "${folder.name}"?`,
        content: 'This deletes everything inside it. This cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: () =>
          deleteFolder.mutate(folder.id, { onSuccess: () => message.success('Folder deleted') }),
      });
    }
  };

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((value) => !value)}
        className="group flex cursor-pointer items-center gap-2 rounded px-2 py-1 pl-4 text-sm hover:bg-black/5 dark:hover:bg-white/5"
      >
        <RightOutlined
          className={`shrink-0 text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
        {expanded ? (
          <FolderOpenOutlined className="shrink-0 text-amber-500" />
        ) : (
          <FolderFilled className="shrink-0 text-amber-500" />
        )}
        <span className="min-w-0 flex-1 truncate">{folder.name}</span>
        <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
          <button
            type="button"
            onClick={(event) => event.stopPropagation()}
            className="shrink-0 rounded px-1 opacity-0 group-hover:opacity-100"
            aria-label="Folder actions"
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>

      {expanded && (
        <FolderTreeLevel
          workspaceId={workspaceId}
          collectionId={folder.collectionId}
          parentId={folder.id}
          depth={depth + 1}
        />
      )}

      <NamePromptModal
        open={renaming}
        title="Rename folder"
        initialValue={folder.name}
        confirmLoading={updateFolder.isPending}
        onCancel={() => setRenaming(false)}
        onSubmit={(name) => updateFolder.mutate({ name }, { onSuccess: () => setRenaming(false) })}
      />

      <NamePromptModal
        open={creatingFolder}
        title="New folder"
        confirmLoading={createFolder.isPending}
        onCancel={() => setCreatingFolder(false)}
        onSubmit={(name) =>
          createFolder.mutate(
            { name, parentId: folder.id },
            { onSuccess: () => setCreatingFolder(false) },
          )
        }
      />

      <FolderDocsModal folder={folder} open={docsOpen} onClose={() => setDocsOpen(false)} />
    </div>
  );
};
